// src/components/mdx/utils/component-utils.client.tsx
//
// Client entry for the MDX component utilities. The presentational fallbacks
// and the server-safe helpers (heading/wrapper/prop utilities + the throttled
// error logger) have a single definition in ./component-utils.shared (B2). This
// entry composes that shared surface and layers the CLIENT-ONLY behavior on
// top: the throttled `logFallbackTrigger` side-effect on each fallback, and the
// Suspense / ErrorBoundary / `next/dynamic` `createSafeMDXComponent` machinery.

'use client'

import { Suspense } from 'react'
import type { ComponentType, ReactNode } from 'react'
import { ErrorBoundary } from '~/components/shared/ErrorBoundary'
import dynamic from 'next/dynamic'
import { useIslandShadow } from '~/components/mdx/wrappers/IslandShadow.client'
import {
  // Server-safe helpers (single definition lives in component-utils.shared).
  logComponentError,
  isValidHeadingContent,
  generateHeadingId,
  Heading,
  createStandardWrapper,
  createClassNameTransformer,
  hasTruthyProp,
  getStringProp,
  type ComponentCategory,
  type ComponentWithDisableDefaultWrapper,
  type HeadingProps,
  // Pure presentational fallback views — wrapped below to add the client-only
  // `logFallbackTrigger` side-effect (the markup is no longer duplicated here).
  ImageFallback as ImageFallbackView,
  QuoteFallback as QuoteFallbackView,
  ChartFallback as ChartFallbackView,
  VideoFallback as VideoFallbackView,
  CardFallback as CardFallbackView,
  TableFallback as TableFallbackView,
  MetricsFallback as MetricsFallbackView,
  CalloutFallback as CalloutFallbackView,
  GridFallback as GridFallbackView,
  CardGridFallback as CardGridFallbackView,
  FlexFallback as FlexFallbackView,
  TabsFallback as TabsFallbackView,
  InteractiveFallback as InteractiveFallbackView,
  AccordionFallback as AccordionFallbackView,
  TimelineFallback as TimelineFallbackView,
  FlowchartFallback as FlowchartFallbackView,
  TextFallback as TextFallbackView,
  TextInlineFallback as TextInlineFallbackView,
  LinkFallback as LinkFallbackView,
  ProviderFallback as ProviderFallbackView,
  UtilityFallback as UtilityFallbackView,
  ComponentErrorFallback as ComponentErrorFallbackView,
  ImageErrorFallback as ImageErrorFallbackView,
  ChartErrorFallback as ChartErrorFallbackView,
  VideoErrorFallback as VideoErrorFallbackView,
  TableErrorFallback as TableErrorFallbackView,
  MetricsErrorFallback as MetricsErrorFallbackView,
  CalloutErrorFallback as CalloutErrorFallbackView,
  TabsErrorFallback as TabsErrorFallbackView,
  AccordionErrorFallback as AccordionErrorFallbackView,
  LinkErrorFallback as LinkErrorFallbackView,
  InlineErrorFallback as InlineErrorFallbackView,
  ProviderErrorFallback as ProviderErrorFallbackView,
  UtilityErrorFallback as UtilityErrorFallbackView,
} from './component-utils.shared'

// Re-export the server-safe helpers so existing `.client` importers keep the
// exact same import surface (single definition now lives in the shared module).
export {
  logComponentError,
  isValidHeadingContent,
  generateHeadingId,
  Heading,
  createStandardWrapper,
  createClassNameTransformer,
  hasTruthyProp,
  getStringProp,
}
export type { ComponentCategory, HeadingProps, ComponentWithDisableDefaultWrapper }

/**
 * ==========================================
 * CLIENT-ONLY FALLBACK LOGGING
 * ==========================================
 */

/**
 * Throttles fallback logging to prevent console flooding.
 * Multiple fallback triggers from the same component will be logged
 * at most once every 10 seconds.
 *
 * @type {Map<string, number>} Map of fallback keys to timestamp of last log
 */
const fallbackLogThrottles = new Map<string, number>()

/**
 * Logs when fallback components are triggered with throttling to prevent flooding the console.
 *
 * @param {string} componentName - The name of the component that triggered the fallback.
 * @param {'loading' | 'error'} fallbackType - The type of fallback that was triggered.
 * @param {string} [reason] - Optional reason for the fallback trigger.
 * @returns {void}
 * @example
 * logFallbackTrigger('Chart', 'loading')
 * logFallbackTrigger('Image', 'error', 'Failed to load image source')
 */
function logFallbackTrigger(componentName: string, fallbackType: 'loading' | 'error', reason?: string): void {
  const key = `${componentName}:${fallbackType}`
  const now = Date.now()
  const lastLogged = fallbackLogThrottles.get(key) ?? 0

  // Only log if we haven't logged this exact fallback in the last 10 seconds
  // This prevents overwhelming the console with repeated fallback logs
  // Dev-only fallback telemetry. `logFallbackTrigger` is called from the
  // fallback components' RENDER bodies, so draining it would POST to /api/log on
  // render — and on an MDX-heavy page (many distinct islands) the per-key
  // throttle can't bound the total. Crucially it's also redundant: the ACTUAL
  // component faults are already drained via `logComponentError` (the shared
  // throttled error logger) and the error boundaries. So this "a fallback
  // rendered" signal stays a local dev console line, never a prod drain.
  if (process.env.NODE_ENV === 'development' && now - lastLogged > 10000) {
    const message = `MDX component "${componentName}" triggered ${fallbackType} fallback${reason ? `: ${reason}` : ''}`
    const logData = {
      type: 'MDX_FALLBACK_TRIGGERED',
      component: componentName,
      fallbackType,
      reason,
    }
    if (fallbackType === 'error') {
      console.warn(message, logData)
    } else {
      console.info(message, logData)
    }
    fallbackLogThrottles.set(key, now)
  }
}

/**
 * ==========================================
 * FALLBACK COMPONENTS (loading)
 * ==========================================
 * Thin client wrappers: log the trigger, then render the shared pure markup.
 */

export const ImageFallback = (): JSX.Element => {
  logFallbackTrigger('Image', 'loading')
  return <ImageFallbackView />
}
export const QuoteFallback = (): JSX.Element => {
  logFallbackTrigger('Quote', 'loading')
  return <QuoteFallbackView />
}
export const ChartFallback = (): JSX.Element => {
  logFallbackTrigger('Chart', 'loading')
  return <ChartFallbackView />
}
export const VideoFallback = (): JSX.Element => {
  logFallbackTrigger('Video', 'loading')
  return <VideoFallbackView />
}
export const CardFallback = (): JSX.Element => {
  logFallbackTrigger('Card', 'loading')
  return <CardFallbackView />
}
export const TableFallback = (): JSX.Element => {
  logFallbackTrigger('Table', 'loading')
  return <TableFallbackView />
}
export const MetricsFallback = (): JSX.Element => {
  logFallbackTrigger('Metrics', 'loading')
  return <MetricsFallbackView />
}
export const CalloutFallback = (): JSX.Element => {
  logFallbackTrigger('Callout', 'loading')
  return <CalloutFallbackView />
}
export const GridFallback = (): JSX.Element => {
  logFallbackTrigger('Grid', 'loading')
  return <GridFallbackView />
}
export const CardGridFallback = (): JSX.Element => {
  logFallbackTrigger('CardGrid', 'loading')
  return <CardGridFallbackView />
}
export const FlexFallback = (): JSX.Element => {
  logFallbackTrigger('Flex', 'loading')
  return <FlexFallbackView />
}
export const TabsFallback = (): JSX.Element => {
  logFallbackTrigger('Tabs', 'loading')
  return <TabsFallbackView />
}
export const InteractiveFallback = (): JSX.Element => {
  logFallbackTrigger('Interactive', 'loading')
  return <InteractiveFallbackView />
}
export const AccordionFallback = (): JSX.Element => {
  logFallbackTrigger('Accordion', 'loading')
  return <AccordionFallbackView />
}
export const TimelineFallback = (): JSX.Element => {
  logFallbackTrigger('Timeline', 'loading')
  return <TimelineFallbackView />
}
export const FlowchartFallback = (): JSX.Element => {
  logFallbackTrigger('Flowchart', 'loading')
  return <FlowchartFallbackView />
}
export const TextFallback = (): JSX.Element => {
  logFallbackTrigger('Text', 'loading')
  return <TextFallbackView />
}
export const TextInlineFallback = (): JSX.Element => {
  logFallbackTrigger('TextInline', 'loading')
  return <TextInlineFallbackView />
}
export const LinkFallback = (): JSX.Element => {
  logFallbackTrigger('Link', 'loading')
  return <LinkFallbackView />
}
export const ProviderFallback = (): JSX.Element => {
  logFallbackTrigger('Provider', 'loading')
  return <ProviderFallbackView />
}
export const UtilityFallback = (): JSX.Element => {
  logFallbackTrigger('Utility', 'loading')
  return <UtilityFallbackView />
}

/**
 * ==========================================
 * FALLBACK COMPONENTS (error)
 * ==========================================
 */

interface ComponentErrorFallbackProps {
  componentName: string
}

export const ComponentErrorFallback = ({ componentName }: ComponentErrorFallbackProps): JSX.Element => {
  logFallbackTrigger(componentName, 'error', 'Generic component error')
  return <ComponentErrorFallbackView componentName={componentName} />
}
export const ImageErrorFallback = (): JSX.Element => {
  logFallbackTrigger('Image', 'error', 'Failed to load image')
  return <ImageErrorFallbackView />
}
export const ChartErrorFallback = (): JSX.Element => {
  logFallbackTrigger('Chart', 'error', 'Failed to load chart')
  return <ChartErrorFallbackView />
}
export const VideoErrorFallback = (): JSX.Element => {
  logFallbackTrigger('Video', 'error', 'Failed to load video')
  return <VideoErrorFallbackView />
}
export const TableErrorFallback = (): JSX.Element => {
  logFallbackTrigger('Table', 'error', 'Failed to load table')
  return <TableErrorFallbackView />
}
export const MetricsErrorFallback = (): JSX.Element => {
  logFallbackTrigger('Metrics', 'error', 'Failed to load metrics')
  return <MetricsErrorFallbackView />
}
export const CalloutErrorFallback = (): JSX.Element => {
  logFallbackTrigger('Callout', 'error', 'Failed to load callout')
  return <CalloutErrorFallbackView />
}
export const TabsErrorFallback = (): JSX.Element => {
  logFallbackTrigger('Tabs', 'error', 'Failed to load tabs')
  return <TabsErrorFallbackView />
}
export const AccordionErrorFallback = (): JSX.Element => {
  logFallbackTrigger('Accordion', 'error', 'Failed to load accordion')
  return <AccordionErrorFallbackView />
}
export const LinkErrorFallback = (): JSX.Element => {
  logFallbackTrigger('Link', 'error', 'Failed to load link')
  return <LinkErrorFallbackView />
}
export const InlineErrorFallback = (): JSX.Element => {
  logFallbackTrigger('Inline', 'error', 'Failed to load inline component')
  return <InlineErrorFallbackView />
}
export const ProviderErrorFallback = (): JSX.Element => {
  logFallbackTrigger('Provider', 'error', 'Provider component failed')
  return <ProviderErrorFallbackView />
}
export const UtilityErrorFallback = (): JSX.Element => {
  logFallbackTrigger('Utility', 'error', 'Utility component failed')
  return <UtilityErrorFallbackView />
}

/**
 * ==========================================
 * SAFE COMPONENT WRAPPERS (client-only)
 * ==========================================
 */

/**
 * Options for creating a safe component wrapper
 * @interface SafeComponentOptions
 * @property {string} componentName - Human-readable component name for error reporting
 * @property {ReactNode} fallback - Loading fallback element
 * @property {ReactNode} errorFallback - Error fallback content to display
 */
export interface SafeComponentOptions {
  /** Human-readable component name for error reporting */
  componentName: string
  /** Loading fallback element */
  fallback: ReactNode
  /** Error fallback content to display */
  errorFallback: ReactNode
}

/**
 * Creates a component wrapped with error boundary and suspense
 * This provides safe rendering of components that might fail
 *
 * @param {ComponentType<T>} Component - The component to wrap
 * @param {SafeComponentOptions} options - Options for the safe component
 * @returns {ComponentType<T & { className?: string }>} A wrapped component with error handling
 * @template T - The props type of the component being wrapped
 */
export function createSafeComponent<T>(
  Component: ComponentType<T>,
  options: SafeComponentOptions
): ComponentType<T & { className?: string }> {
  const { componentName, fallback, errorFallback } = options

  // Return a new component that wraps the original with error boundary and suspense
  const SafeComponent = function SafeComponent(props: T & { className?: string }): JSX.Element {
    // Create a custom error boundary fallback that includes component info
    const wrappedErrorFallback = (error: Error): ReactNode => {
      // Use throttled logging to prevent console flooding
      logComponentError(componentName, error)

      // Return the error fallback UI
      return errorFallback
    }

    return (
      <ErrorBoundary fallback={wrappedErrorFallback}>
        <Suspense fallback={fallback}>
          <Component {...props} />
        </Suspense>
      </ErrorBoundary>
    )
  }

  SafeComponent.displayName = `Safe(${componentName})`
  return SafeComponent
}

/**
 * Options for creating a safe hierarchical component wrapper
 * Extended with options for preserving children on error
 *
 * @interface SafeHierarchicalComponentOptions
 * @extends SafeComponentOptions
 * @property {boolean} [preserveChildrenOnError] - Whether to preserve children when parent component fails
 */
export interface SafeHierarchicalComponentOptions extends SafeComponentOptions {
  /** Whether to preserve children when parent component fails */
  preserveChildrenOnError?: boolean
}

/**
 * Creates a hierarchical component wrapped with error boundary and suspense
 * This provides safe rendering of components that might fail while
 * optionally preserving their children content
 *
 * @param {ComponentType<T>} Component - The component to wrap
 * @param {SafeHierarchicalComponentOptions} options - Options for the safe hierarchical component
 * @returns {ComponentType<T & { className?: string, children?: ReactNode }>} A wrapped hierarchical component with error handling
 * @template T - The props type of the component being wrapped
 */
export function createSafeHierarchicalComponent<T>(
  Component: ComponentType<T>,
  options: SafeHierarchicalComponentOptions
): ComponentType<T & { className?: string; children?: ReactNode }> {
  const {
    componentName,
    fallback,
    errorFallback,
    preserveChildrenOnError = false
  } = options

  const SafeHierarchicalComponent = function SafeHierarchicalComponent(props: T & {
    className?: string
    children?: ReactNode
  }): JSX.Element {
    // Destructure children for potential preservation
    const { children, ...restProps } = props

    // Handle errors within the component
    const wrappedErrorFallback = (error: Error): ReactNode => {
      // Log the error for debugging and monitoring
      logComponentError(componentName, error)

      // If we should preserve children on error, render them with the fallback
      if (preserveChildrenOnError && children) {
        return (
          <>
            {errorFallback}
            <div className="mt-4">
              {children}
            </div>
          </>
        )
      }

      // Otherwise, just return the error fallback
      return errorFallback
    }

    return (
      <ErrorBoundary fallback={wrappedErrorFallback}>
        <Suspense fallback={fallback}>
          <Component {...restProps as T}>
            {children}
          </Component>
        </Suspense>
      </ErrorBoundary>
    )
  }

  SafeHierarchicalComponent.displayName = `SafeHierarchical(${componentName})`
  return SafeHierarchicalComponent
}

/**
 * Options for creating a safe MDX component
 * @interface SafeMDXComponentOptions
 */
export interface SafeMDXComponentOptions {
  /** Human-readable component name for error reporting */
  componentName: string

  /** Component category for standardized fallbacks */
  category: ComponentCategory

  /** Whether the component should be server-side rendered */
  ssr?: boolean

  /** Whether the component has a hierarchical structure */
  isHierarchical?: boolean

  /** For hierarchical components, whether children should be preserved on error */
  preserveChildrenOnError?: boolean

  /** Optional custom loading fallback to override the default */
  customLoadingFallback?: ReactNode

  /** Optional custom error fallback to override the default */
  customErrorFallback?: ReactNode

  /**
   * Optional transformer function to apply additional transformations to the component
   * This allows for adding wrappers, merging classNames, or other prop manipulations
   * The transformer receives the loaded component and returns a new component
   */
  transformer?: <P extends JSX.IntrinsicAttributes>(Component: ComponentType<P>) => ComponentType<P>

  /** Whether to disable the default my-8 wrapper */
  disableDefaultWrapper?: boolean
}

/**
 * Creates a safe MDX component with standardized fallbacks
 * Handles dynamic imports, error boundaries, and proper loading states
 *
 * @param {() => Promise<{ default: ComponentType<T> } | ComponentType<T>>} importFn - Function that imports the component
 * @param {SafeMDXComponentOptions} options - Options for the safe MDX component
 * @returns {ComponentType<T & { className?: string, children?: ReactNode }>} A wrapped MDX component with error handling and loading states
 * @template T - The props type of the component being wrapped
 */
export function createSafeMDXComponent<T>(
  importFn: () => Promise<{ default: ComponentType<T> } | ComponentType<T>>,
  options: SafeMDXComponentOptions
): ComponentType<T & { className?: string; children?: ReactNode }> {
  const {
    componentName,
    category,
    ssr = false,
    isHierarchical = false,
    preserveChildrenOnError = false,
    customLoadingFallback,
    customErrorFallback,
    transformer,
    disableDefaultWrapper = false
  } = options

  // Determine appropriate fallbacks based on component category
  const LoadingFallback = getLoadingFallback(category, componentName, customLoadingFallback)

  // `next/dynamic` `loading` for the inner leaf. When this Safe* component is
  // rendered via `makeClientSSR` (Table/Chart islands), the crawlable shadow is
  // available in `IslandShadowContext`, so we render THAT during the inner
  // suspension instead of the generic skeleton — otherwise a cold leaf chunk
  // would flash the headerless/rowless skeleton over the just-restored shadow
  // (#372 r3461700401). When there is no provider (Safe* used standalone),
  // `useIslandShadow()` is `null` and we fall back to the category skeleton, so
  // standalone behaviour is unchanged.
  function SafeLeafLoading(): ReactNode {
    const shadow = useIslandShadow()
    return shadow != null ? <>{shadow}</> : <LoadingFallback />
  }

  // Get the appropriate error fallback component based on category
  const ErrorFallbackComponent = getErrorFallback(category, customErrorFallback)

  // Create dynamic component with error handling
  const DynamicComponent = dynamic(
    async () => {
      try {
        // Import the component
        const imported = await importFn()

        // Extract the component (handle both default and named exports)
        let Component: ComponentType<T> = 'default' in imported ? imported.default : imported

        // Apply custom transformer if provided
        if (transformer) {
          Component = transformer(Component as unknown as ComponentType<JSX.IntrinsicAttributes>) as unknown as ComponentType<T>
        }

        // Apply default wrapper unless disabled
        if (!disableDefaultWrapper) {
          Component = createStandardWrapper()(Component as unknown as ComponentType<JSX.IntrinsicAttributes>) as unknown as ComponentType<T>
        }

        return Component
      } catch (error) {
        // Log the error for debugging and monitoring
        logComponentError(componentName, error instanceof Error ? error : new Error(String(error)))

        // Rethrow to let error boundary handle it
        throw error
      }
    },
    {
      loading: SafeLeafLoading,
      ssr
    }
  )

  // Create the Safe component with appropriate error handling based on hierarchy
  if (isHierarchical) {
    // Use hierarchical error handling for components that might have children
    const SafeReturn = createSafeHierarchicalComponent<T>(DynamicComponent, {
      componentName,
      fallback: <LoadingFallback />,
      errorFallback: <ErrorFallbackComponent componentName={componentName} />,
      preserveChildrenOnError
    })
    // Mark block/inline nature for MDX paragraph renderer
    ;(SafeReturn as unknown as Record<string, unknown>).mdxIsBlock = ((): boolean => {
      // Treat text/link/inline categories as inline; everything else is block-level
      return !(category === 'inline' || category === 'link' || category === 'text')
    })()
    return SafeReturn
  } else {
    // Use standard error handling for simple components
    const SafeReturn = createSafeComponent<T>(DynamicComponent, {
      componentName,
      fallback: <LoadingFallback />,
      errorFallback: <ErrorFallbackComponent componentName={componentName} />
    })
    ;(SafeReturn as unknown as Record<string, unknown>).mdxIsBlock = ((): boolean => {
      return !(category === 'inline' || category === 'link' || category === 'text')
    })()
    return SafeReturn
  }
}

/**
 * Selects the appropriate loading fallback component based on component category
 *
 * @param {ComponentCategory} category - The category of the component
 * @param {string} componentName - The name of the component for display purposes
 * @param {ReactNode} [customFallback] - Optional custom fallback component
 * @returns {ComponentType<unknown>} The fallback component to use
 */
function getLoadingFallback(
  category: ComponentCategory,
  componentName: string,
  customFallback?: ReactNode
): ComponentType<unknown> & ComponentWithDisableDefaultWrapper {
  if (customFallback) {
    // If a custom fallback is provided, use it
    const CustomFallback = function CustomFallbackComponent() {
      logFallbackTrigger(componentName, 'loading', 'Custom loading fallback')
      return customFallback as JSX.Element
    } as ComponentType<unknown> & ComponentWithDisableDefaultWrapper
    CustomFallback.displayName = `${componentName}LoadingFallback`
    CustomFallback.disableDefaultWrapper = true
    return CustomFallback
  }

  // Otherwise, select based on component category
  let FallbackComponent: ComponentType<unknown>

  switch (category) {
    case 'image':
      FallbackComponent = function ImageFallbackComponent() { return <ImageFallback /> }
      FallbackComponent.displayName = 'ImageFallbackComponent'
      break
    case 'chart':
      FallbackComponent = function ChartFallbackComponent() { return <ChartFallback /> }
      FallbackComponent.displayName = 'ChartFallbackComponent'
      break
    case 'table':
      FallbackComponent = function TableFallbackComponent() { return <TableFallback /> }
      FallbackComponent.displayName = 'TableFallbackComponent'
      break
    case 'card':
      FallbackComponent = function CardFallbackComponent() { return <CardFallback /> }
      FallbackComponent.displayName = 'CardFallbackComponent'
      break
    case 'metrics':
      FallbackComponent = function MetricsFallbackComponent() { return <MetricsFallback /> }
      FallbackComponent.displayName = 'MetricsFallbackComponent'
      break
    case 'media':
      FallbackComponent = function VideoFallbackComponent() { return <VideoFallback /> }
      FallbackComponent.displayName = 'VideoFallbackComponent'
      break
    case 'quote':
      FallbackComponent = function QuoteFallbackComponent() { return <QuoteFallback /> }
      FallbackComponent.displayName = 'QuoteFallbackComponent'
      break
    case 'interactive':
      FallbackComponent = function InteractiveFallbackComponent() { return <InteractiveFallback /> }
      FallbackComponent.displayName = 'InteractiveFallbackComponent'
      break
    case 'layout':
      FallbackComponent = function GridFallbackComponent() { return <GridFallback /> }
      FallbackComponent.displayName = 'GridFallbackComponent'
      break
    case 'link':
      FallbackComponent = function LinkFallbackComponent() { return <LinkFallback /> }
      FallbackComponent.displayName = 'LinkFallbackComponent'
      break
    case 'inline':
      FallbackComponent = function TextInlineFallbackComponent() { return <TextInlineFallback /> }
      FallbackComponent.displayName = 'TextInlineFallbackComponent'
      break
    case 'provider':
      FallbackComponent = function ProviderFallbackComponent() { return <ProviderFallback /> }
      FallbackComponent.displayName = 'ProviderFallbackComponent'
      break
    case 'utility':
      FallbackComponent = function UtilityFallbackComponent() { return <UtilityFallback /> }
      FallbackComponent.displayName = 'UtilityFallbackComponent'
      break
    case 'text':
    default:
      FallbackComponent = function TextFallbackComponent() { return <TextFallback /> }
      FallbackComponent.displayName = 'TextFallbackComponent'
      break
  }

  // Cast to the component type with our additional property
  const FallbackWithProperty = FallbackComponent as ComponentType<unknown> & ComponentWithDisableDefaultWrapper

  // Add static property to ALL fallback components to prevent p tag wrapping
  FallbackWithProperty.disableDefaultWrapper = true

  // Set a specific displayName for better debugging
  FallbackWithProperty.displayName = `${componentName}Loading`
  return FallbackWithProperty
}

/**
 * Selects the appropriate error fallback component based on component category
 *
 * @param {ComponentCategory} category - The category of the component
 * @param {ReactNode} [customFallback] - Optional custom fallback component
 * @returns {ComponentType<{ componentName: string }>} The error fallback component to use
 */
function getErrorFallback(
  category: ComponentCategory,
  customFallback?: ReactNode
): ComponentType<{ componentName: string }> {
  if (customFallback) {
    // If a custom fallback is provided, use it
    const CustomErrorFallback = function CustomErrorFallbackComponent(): JSX.Element {
      return customFallback as JSX.Element
    }
    CustomErrorFallback.displayName = 'CustomErrorFallbackComponent'
    return CustomErrorFallback
  }

  // Define all error fallback components outside of the switch statement
  const ImageErrorFallbackComponent = function ImageErrorFallbackComponent(): JSX.Element {
    return <ImageErrorFallback />
  }
  ImageErrorFallbackComponent.displayName = 'ImageErrorFallbackComponent'

  const ChartErrorFallbackComponent = function ChartErrorFallbackComponent(): JSX.Element {
    return <ChartErrorFallback />
  }
  ChartErrorFallbackComponent.displayName = 'ChartErrorFallbackComponent'

  const TableErrorFallbackComponent = function TableErrorFallbackComponent(): JSX.Element {
    return <TableErrorFallback />
  }
  TableErrorFallbackComponent.displayName = 'TableErrorFallbackComponent'

  const MetricsErrorFallbackComponent = function MetricsErrorFallbackComponent(): JSX.Element {
    return <MetricsErrorFallback />
  }
  MetricsErrorFallbackComponent.displayName = 'MetricsErrorFallbackComponent'

  const VideoErrorFallbackComponent = function VideoErrorFallbackComponent(): JSX.Element {
    return <VideoErrorFallback />
  }
  VideoErrorFallbackComponent.displayName = 'VideoErrorFallbackComponent'

  const LinkErrorFallbackComponent = function LinkErrorFallbackComponent(): JSX.Element {
    return <LinkErrorFallback />
  }
  LinkErrorFallbackComponent.displayName = 'LinkErrorFallbackComponent'

  const InlineErrorFallbackComponent = function InlineErrorFallbackComponent(): JSX.Element {
    return <InlineErrorFallback />
  }
  InlineErrorFallbackComponent.displayName = 'InlineErrorFallbackComponent'

  const ProviderErrorFallbackComponent = function ProviderErrorFallbackComponent(): JSX.Element {
    return <ProviderErrorFallback />
  }
  ProviderErrorFallbackComponent.displayName = 'ProviderErrorFallbackComponent'

  const UtilityErrorFallbackComponent = function UtilityErrorFallbackComponent(): JSX.Element {
    return <UtilityErrorFallback />
  }
  UtilityErrorFallbackComponent.displayName = 'UtilityErrorFallbackComponent'

  switch (category) {
    case 'image':
      return ImageErrorFallbackComponent
    case 'chart':
      return ChartErrorFallbackComponent
    case 'table':
      return TableErrorFallbackComponent
    case 'metrics':
      return MetricsErrorFallbackComponent
    case 'media':
      return VideoErrorFallbackComponent
    case 'link':
      return LinkErrorFallbackComponent
    case 'inline':
      return InlineErrorFallbackComponent
    case 'provider':
      return ProviderErrorFallbackComponent
    case 'utility':
      return UtilityErrorFallbackComponent
    case 'layout':
      return ComponentErrorFallback
    case 'text':
      return ComponentErrorFallback
    case 'card':
      return ComponentErrorFallback
    case 'quote':
      return ComponentErrorFallback
    case 'interactive':
      return ComponentErrorFallback
    default:
      return ComponentErrorFallback
  }
}

/**
 * Collection of utility functions and components for MDX rendering
 */
export const componentUtils = {
  // Fallbacks
  ImageFallback,
  QuoteFallback,
  ChartFallback,
  VideoFallback,
  CardFallback,
  TableFallback,
  MetricsFallback,
  TextInlineFallback,

  // Error fallbacks
  ComponentErrorFallback,
  ImageErrorFallback,
  ChartErrorFallback,
  VideoErrorFallback,
  TableErrorFallback,
  MetricsErrorFallback,

  // Helper functions
  logComponentError,
  logFallbackTrigger,
  isValidHeadingContent,
  generateHeadingId,
  hasTruthyProp,
  getStringProp,

  // Component creation functions
  createSafeComponent,
  createSafeHierarchicalComponent,
  createSafeMDXComponent,

  // Components
  Heading,
  getLoadingFallback,
  getErrorFallback,
  createStandardWrapper,
  createClassNameTransformer
}

// Export the utilities as the default export
export default componentUtils
